import { FC, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import AutomasPeritajeEntity from "../../../../domain/entities/AutomasPeritajeEntity";
import TypeVehicleContext from "../../../../domain/providers/typeVehicle/TypeVehicleContext";
import TypeVehicleContextType from "../../../../domain/providers/typeVehicle/TypeVehicleContextType";
import { buildPeritajeVM, CampoVM, CategoriaVM, MedicionVM, PeritajeVM, TarjetaVM } from "./peritajeViewModel";
import { BloqueNovedadesVM, HojaNovedadesVM } from "./paginarNovedades";
import { MedidasHojas, paginarHojas, PiezaVM, SIN_MEDIDAS, sondaHojas } from "./paginarHojas";
import { DASH, fmtScore, isEmpty } from "./utils";
import "./CertificadoPeritajeAutomasStyles.scss";

/** Hoja A4 a 96 dpi. El Figma está a 300 dpi (2480 × 3508), de ahí el factor 3.125 del SCSS. */
export const PAGE_WIDTH = 794;
export const PAGE_HEIGHT = 1123;

const LOGO_AUTOMAS = "/assets/peritaje/logo-automas.png";
const ICONO_CHECK = "/assets/peritaje/check-estandar.svg";
const ICONO_DIAGNOSTICO = "/assets/peritaje/diag-carroceria.svg";
/** El mismo lockup que usa el navbar y el footer, para no tener dos logos de marca distintos. */
const LOGO_WCAR = "/assets/logos/horizontal.svg";

interface CertificadoPeritajeAutomasProps {
    data: AutomasPeritajeEntity;
    /** Anexo con el resto de fotos del peritaje, después de los detalles. */
    showAnnex?: boolean;
    className?: string;
}

/** Foto del peritaje: la franja naranja del diseño con la imagen encima. */
const Foto: FC<{ src?: string; alt: string }> = ({ src, alt }) => {
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setFailed(false);
    }, [src]);

    return (
        <figure className="pa-foto">
            <div className="pa-foto__marco">
                {isEmpty(src) || failed ? (
                    <span className="pa-foto__vacia">Sin fotografía</span>
                ) : (
                    <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
                )}
            </div>
        </figure>
    );
};

/** Cabecera común a todas las hojas: logo, sello y número de acta. */
const Cabecera: FC<{ vm: PeritajeVM }> = ({ vm }) => (
    <header className="pa-cabecera">
        <img className="pa-cabecera__logo" src={LOGO_AUTOMAS} alt="Automás" />
        <span className="pa-cabecera__sep" />
        <img className="pa-cabecera__wcar" src={LOGO_WCAR} alt="Wcar" />
        <span className="pa-cabecera__punto" aria-hidden="true">
            ·
        </span>
        <p className="pa-cabecera__sello">Peritaje oficial</p>
        <div className="pa-cabecera__acta">
            <p>
                Acta No. <b>{vm.acta}</b>
            </p>
            <span>{vm.fecha}</span>
        </div>
    </header>
);

/** Hoja A4 con su cabecera. `pa-hoja` es lo que se escala en pantalla y se pagina al imprimir. */
const Hoja: FC<{ vm: PeritajeVM; children: ReactNode }> = ({ vm, children }) => (
    <section className="pa-hoja">
        <Cabecera vm={vm} />
        {children}
    </section>
);

/** Tarjeta del diseño: franja naranja asomando por encima de la superficie gris. */
const Tarjeta: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
    <div className={`pa-tarjeta ${className ?? ""}`}>
        <div className="pa-tarjeta__cuerpo">{children}</div>
    </div>
);

const TituloSeccion: FC<{ titulo: string; descripcion?: string; icono?: boolean }> = ({
    titulo,
    descripcion,
    icono,
}) => (
    <div className="pa-seccion">
        {icono && <img className="pa-seccion__icono" src={ICONO_DIAGNOSTICO} alt="" aria-hidden="true" />}
        <div className="pa-seccion__texto">
            <h2>{titulo}</h2>
            {descripcion && <p>{descripcion}</p>}
        </div>
    </div>
);

const Leyenda: FC = () => (
    <ul className="pa-leyenda">
        <li>
            <span className="pa-leyenda__punto pa-leyenda__punto--good" />
            Sin novedades
        </li>
        <li aria-hidden="true" className="pa-leyenda__bullet">
            •
        </li>
        <li>
            <span className="pa-leyenda__punto pa-leyenda__punto--fair" />
            Leves
        </li>
        <li aria-hidden="true" className="pa-leyenda__bullet">
            •
        </li>
        <li>
            <span className="pa-leyenda__punto pa-leyenda__punto--bad" />
            Requiere atención
        </li>
    </ul>
);

const FilaDiagnostico: FC<{ categoria: CategoriaVM }> = ({ categoria }) => (
    <li className="pa-diag">
        <span className="pa-diag__icono">
            <img src={categoria.icono} alt="" aria-hidden="true" />
        </span>
        <div className="pa-diag__texto">
            <h3>{categoria.label}</h3>
            <p>{categoria.detalle}</p>
        </div>
        <b className={`pa-score pa-diag__score pa-score--${categoria.tono}`}>{categoria.resumen}</b>
    </li>
);

/** Un grupo de hallazgos, o el tramo de él que cae en esta columna. */
const GrupoNovedades: FC<{ bloque: BloqueNovedadesVM }> = ({ bloque }) => (
    <section className="pa-novedades__grupo">
        <h3>
            {bloque.titulo}
            {bloque.continua && <span> (cont.)</span>}
        </h3>
        <ul>
            {bloque.items.map((item, index) => (
                <li key={`${item.pieza}-${index}`}>
                    <span>{item.pieza}</span>
                    <b className={`pa-novedades__estado pa-novedades__estado--${item.tono}`}>{item.estado}</b>
                </li>
            ))}
        </ul>
    </section>
);

/** Detalle de los hallazgos en dos columnas. Si viene de la hoja anterior lleva `continuacion`. */
const TarjetaNovedades: FC<{ columnas: HojaNovedadesVM; total: number; continuacion: boolean }> = ({
    columnas,
    total,
    continuacion,
}) => (
    <Tarjeta className="pa-tarjeta--novedades">
        <h2 className="pa-tarjeta__titulo pa-tarjeta__titulo--barra">
            NOVEDADES ENCONTRADAS <span>{`${total} en total${continuacion ? " · continuación" : ""}`}</span>
        </h2>
        <div className="pa-novedades">
            {columnas.map((columna, index) => (
                <div className="pa-novedades__columna" key={index}>
                    {columna.map((bloque) => (
                        <GrupoNovedades key={bloque.titulo} bloque={bloque} />
                    ))}
                </div>
            ))}
        </div>
    </Tarjeta>
);

const FilaMedicion: FC<{ fila: MedicionVM }> = ({ fila }) => (
    <li className="pa-medicion">
        <span>{fila.label}</span>
        <b className={`pa-score pa-score--${fila.tono}`}>{fmtScore(fila.percentage)}</b>
    </li>
);

const TarjetaMedicion: FC<{ tarjeta: TarjetaVM }> = ({ tarjeta }) => (
    <Tarjeta className="pa-tarjeta--medicion">
        <h2 className="pa-tarjeta__titulo">{tarjeta.titulo}</h2>
        <p className="pa-tarjeta__descripcion">{tarjeta.descripcion}</p>
        <ul className="pa-mediciones">
            {tarjeta.filas.map((fila) => (
                <FilaMedicion key={fila.label} fila={fila} />
            ))}
        </ul>
    </Tarjeta>
);

/** Lo fijo de la hoja 1: resumen, fotos destacadas, diagnóstico y mediciones. */
const Portada: FC<{ vm: PeritajeVM; tipoFoto?: string }> = ({ vm, tipoFoto }) => (
    <>
        <div className="pa-resumen">
            <div className="pa-resumen__placa">
                <span>PLACA</span>
                <b>{vm.placa}</b>
            </div>
            <h1 className="pa-resumen__titulo">{vm.titulo}</h1>
            <div className="pa-resumen__sellos">
                {vm.resultado && (
                    <p className="pa-badge">
                        <img src={ICONO_CHECK} alt="" aria-hidden="true" />
                        {vm.resultado}
                    </p>
                )}
                {/* Sin dato no se pinta nada: hoy `insurable` llega null en todos los peritajes,
                    y un sello vacío en cada certificado sería ruido, no información. */}
                {vm.asegurable !== "desconocido" && (
                    <p className={`pa-badge pa-badge--${vm.asegurable === "si" ? "ok" : "alerta"}`}>
                        Asegurable: <b>{vm.asegurable === "si" ? "Sí" : "No"}</b>
                    </p>
                )}
            </div>
            <span className="pa-resumen__tipo">{tipoFoto && <img src={tipoFoto} alt="" aria-hidden="true" />}</span>
            <div className="pa-resumen__detalle">
                {vm.resumen.map((linea) => (
                    <p key={linea}>{linea}</p>
                ))}
            </div>
            <div className="pa-resumen__servicio">
                {vm.servicio.map((linea) => (
                    <p key={linea}>{linea}</p>
                ))}
            </div>
        </div>

        <div className="pa-galeria">
            {Array.from({ length: 3 }).map((_, index) => (
                <Foto key={index} src={vm.fotos[index]} alt={`Fotografía ${index + 1} del peritaje`} />
            ))}
        </div>

        <Tarjeta className="pa-tarjeta--diagnostico">
            <div className="pa-tarjeta__encabezado">
                <TituloSeccion
                    titulo="DIAGNÓSTICO DE INSPECCIÓN"
                    descripcion="Evaluación integral la cual especifica el estado de las partes del vehículo"
                    icono
                />
                <Leyenda />
            </div>
            <ul className="pa-diagnostico">
                {vm.categorias.map((categoria) => (
                    <FilaDiagnostico key={categoria.label} categoria={categoria} />
                ))}
            </ul>
        </Tarjeta>

        {vm.tarjetas.length > 0 && (
            <div className="pa-mediciones-grid">
                {vm.tarjetas.map((tarjeta) => (
                    <TarjetaMedicion key={tarjeta.titulo} tarjeta={tarjeta} />
                ))}
            </div>
        )}
    </>
);

const TarjetaNotas: FC<{ vm: PeritajeVM }> = ({ vm }) => (
    <Tarjeta className="pa-tarjeta--notas">
        <div className="pa-tarjeta__encabezado">
            <TituloSeccion
                titulo="DIAGNÓSTICO DE INSPECCIÓN"
                descripcion="Evaluación integral la cual especifica el estado de las partes del vehículo"
                icono
            />
        </div>
        <div className="pa-notas">
            <article>
                <h3>Observaciones</h3>
                <p>{vm.observaciones || "Sin observaciones registradas en este peritaje."}</p>
            </article>
        </div>
    </Tarjeta>
);

const AccesoriosYValores: FC<{ vm: PeritajeVM }> = ({ vm }) => (
    <div className="pa-columnas">
        <Tarjeta className="pa-tarjeta--accesorios">
            <h2 className="pa-tarjeta__titulo pa-tarjeta__titulo--barra">ACCESORIOS</h2>
            {vm.accesorios.length ? (
                <table className="pa-accesorios">
                    <thead>
                        <tr>
                            <th scope="col">CANT</th>
                            <th scope="col">DESCRIPCIÓN</th>
                            <th scope="col">MARCA</th>
                            <th scope="col">VALOR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vm.accesorios.map((accesorio, index) => (
                            <tr key={`${accesorio.descripcion}-${index}`}>
                                <td className="pa-accesorios__cant">{accesorio.cantidad}</td>
                                <td>{accesorio.descripcion}</td>
                                <td className="pa-accesorios__marca">
                                    {accesorio.marca === DASH ? <span className="pa-guion" /> : accesorio.marca}
                                </td>
                                <td className="pa-accesorios__valor">{accesorio.valor}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <p className="pa-sin-datos">Este peritaje no registra accesorios.</p>
            )}
        </Tarjeta>

        <Tarjeta className="pa-tarjeta--valores">
            <h2 className="pa-tarjeta__titulo pa-tarjeta__titulo--barra">VALORES</h2>
            <ul className="pa-valores">
                {vm.valores.map((valor) => (
                    <li key={valor.titulo}>
                        <div>
                            <b>{valor.titulo}</b>
                            <span>{valor.subtitulo}</span>
                        </div>
                        <strong>{valor.valor}</strong>
                    </li>
                ))}
            </ul>
        </Tarjeta>
    </div>
);

const TarjetaDetalles: FC<{ vm: PeritajeVM; filas: CampoVM[][]; conTipo: boolean; continuacion: boolean }> = ({
    vm,
    filas,
    conTipo,
    continuacion,
}) => (
    <Tarjeta className="pa-tarjeta--detalles">
        <h2 className="pa-tarjeta__titulo pa-tarjeta__titulo--barra">
            DETALLES DEL VEHÍCULO {continuacion && <span>continuación</span>}
        </h2>
        {conTipo && (
            <div className="pa-campo pa-campo--ancho">
                <span>TIPO</span>
                <b>{vm.tipo}</b>
            </div>
        )}
        <dl className="pa-detalles">
            {filas.flat().map((campo) => (
                <div className="pa-campo" key={campo.label}>
                    <dt>{campo.label}</dt>
                    <dd>{campo.value}</dd>
                </div>
            ))}
        </dl>
    </Tarjeta>
);

const TarjetaAnexo: FC<{ fotos: string[]; numero: number; total: number }> = ({ fotos, numero, total }) => (
    <Tarjeta className="pa-tarjeta--anexo">
        <h2 className="pa-tarjeta__titulo pa-tarjeta__titulo--barra">
            ANEXO FOTOGRÁFICO <span>{`${numero} de ${total}`}</span>
        </h2>
        <div className="pa-anexo">
            {fotos.map((foto, index) => (
                <Foto key={foto} src={foto} alt={`Fotografía ${index + 1} del anexo ${numero}`} />
            ))}
        </div>
    </Tarjeta>
);

const Pieza: FC<{ vm: PeritajeVM; pieza: PiezaVM }> = ({ vm, pieza }) => {
    switch (pieza.tipo) {
        case "novedades":
            return (
                <TarjetaNovedades
                    columnas={pieza.columnas}
                    total={vm.totalNovedades}
                    continuacion={pieza.continuacion}
                />
            );
        case "notas":
            return <TarjetaNotas vm={vm} />;
        case "columnas":
            return <AccesoriosYValores vm={vm} />;
        case "detalles":
            return (
                <TarjetaDetalles
                    vm={vm}
                    filas={pieza.filas}
                    conTipo={pieza.conTipo}
                    continuacion={pieza.continuacion}
                />
            );
        case "anexo":
            return <TarjetaAnexo fotos={pieza.fotos} numero={pieza.numero} total={pieza.total} />;
    }
};

/**
 * Los bloques que le tocaron a una hoja. `pa-flujo` ocupa exactamente el alto que queda libre en
 * la hoja, y eso es lo que se mide como espacio disponible.
 */
const Flujo: FC<{ vm: PeritajeVM; piezas: PiezaVM[]; portada?: boolean }> = ({ vm, piezas, portada }) => (
    <div className={`pa-flujo ${portada ? "pa-flujo--portada" : ""}`}>
        {piezas.map((pieza, index) => (
            <div className="pa-pieza" data-pieza={pieza.tipo} key={`${pieza.tipo}-${index}`}>
                <Pieza vm={vm} pieza={pieza} />
            </div>
        ))}
    </div>
);

/**
 * Mide la primera pasada (`sondaHojas`): el alto libre del flujo en la hoja 1 y en una hoja
 * vacía, y lo que ocupa cada bloque. Devuelve px de la hoja sin escalar, o `null` si no hay
 * layout (por ejemplo en jsdom, donde todo mide 0).
 */
const medirHojas = (contenedor: HTMLElement | null): MedidasHojas | null => {
    const [flujoPrimera, flujoResto] = Array.from(contenedor?.querySelectorAll<HTMLElement>(".pa-flujo") ?? []);
    const hoja = flujoPrimera?.closest<HTMLElement>(".pa-hoja");
    if (!flujoResto || !hoja?.offsetHeight) return null;

    // La hoja va escalada con `transform`, que sí afecta a getBoundingClientRect.
    const escala = hoja.getBoundingClientRect().height / hoja.offsetHeight;
    if (!escala) return null;

    const alto = (elemento?: Element | null) => (elemento ? elemento.getBoundingClientRect().height / escala : 0);
    // Los estilos calculados no pasan por el `transform`: ya vienen en px de la hoja.
    const px = (elemento: Element | null | undefined, propiedad: string) =>
        elemento ? parseFloat(getComputedStyle(elemento).getPropertyValue(propiedad)) || 0 : 0;
    const pieza = (tipo: PiezaVM["tipo"]) => flujoPrimera.querySelector(`[data-pieza="${tipo}"]`);
    const marco = (tarjeta: Element | null, interior: string) => alto(tarjeta) - alto(tarjeta?.querySelector(interior));

    const novedades = pieza("novedades");
    const detalles = pieza("detalles");
    const tipo = detalles?.querySelector(".pa-campo--ancho");
    const anexo = pieza("anexo");

    return {
        // 1 px de holgura para que el redondeo nunca deje asomar el borde de la última tarjeta.
        primera: alto(flujoPrimera) - 1,
        resto: alto(flujoResto) - 1,
        separacion: px(flujoPrimera, "row-gap"),
        notas: alto(pieza("notas")),
        columnas: alto(pieza("columnas")),
        novedades: {
            marco: marco(novedades, ".pa-novedades"),
            titulo: alto(novedades?.querySelector(".pa-novedades h3")),
            item: alto(novedades?.querySelector(".pa-novedades li")),
        },
        detalles: {
            marcoConTipo: marco(detalles, ".pa-detalles"),
            marcoSinTipo: marco(detalles, ".pa-detalles") - alto(tipo) - px(tipo, "margin-bottom"),
            fila: alto(detalles?.querySelector(".pa-detalles .pa-campo")),
        },
        anexo: {
            marco: marco(anexo, ".pa-anexo"),
            fila: alto(anexo?.querySelector(".pa-foto")),
            separacion: px(anexo?.querySelector(".pa-anexo"), "row-gap"),
        },
    };
};

/**
 * Certificado de peritaje de Automás (diseño Figma `Wcar TECH peritaje`, nodos 3754:7307 y
 * 3755:7820).
 *
 * Cada hoja se dibuja siempre a 794 px de ancho (A4 a 96 dpi) y se escala para caber en el
 * contenedor: así el diseño no se deforma en el modal y al imprimir vuelve a su tamaño real.
 */
const CertificadoPeritajeAutomas: FC<CertificadoPeritajeAutomasProps> = ({ data, showAnnex = true, className }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const resize = () => {
            const available = element.clientWidth;
            if (available > 0) setScale(Math.min(1, available / PAGE_WIDTH));
        };

        resize();
        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", resize);
            return () => window.removeEventListener("resize", resize);
        }
        const observer = new ResizeObserver(resize);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    // La silueta del vehículo sale del catálogo de tipos que ya carga la app (el mismo del
    // detalle del carro), en vez de guardar copias de esas imágenes en el certificado.
    const { typeVehicles } = useContext(TypeVehicleContext) as TypeVehicleContextType;
    const vm = useMemo(() => buildPeritajeVM(data), [data]);
    const tipoFoto = useMemo(
        () => typeVehicles.find((tipo) => tipo.name?.trim().toLowerCase() === vm.tipoVehiculo.toLowerCase())?.photo,
        [typeVehicles, vm.tipoVehiculo],
    );

    // El reparto en hojas va en dos pasadas: la primera pinta una muestra de cada bloque y la
    // mide, la segunda pinta el reparto real. Las dos ocurren en efectos de layout, antes de que
    // el navegador pinte, así que la muestra nunca se ve.
    const [medidas, setMedidas] = useState<MedidasHojas | null>(null);

    useLayoutEffect(() => setMedidas(null), [data, showAnnex]);

    useLayoutEffect(() => {
        if (medidas) return;
        setMedidas(medirHojas(containerRef.current) ?? SIN_MEDIDAS);
    }, [medidas]);

    // Con la fuente de respaldo los textos envuelven distinto y los altos cambian: al terminar
    // de cargar Urbanist/Poppins se vuelve a medir.
    useEffect(() => {
        if (document.fonts?.status !== "loading") return;
        let active = true;
        document.fonts.ready.then(() => active && setMedidas(null));
        return () => {
            active = false;
        };
    }, []);

    const hojas = useMemo(() => {
        const anexo = showAnnex ? vm.anexo : [];
        return medidas ? paginarHojas(vm, anexo, medidas) : sondaHojas(vm, anexo);
    }, [vm, showAnnex, medidas]);

    return (
        <div className={`pa-doc ${className ?? ""}`} ref={containerRef}>
            {hojas.map((piezas, index) => (
                <div
                    className="pa-hoja-wrap"
                    key={index}
                    style={{ width: PAGE_WIDTH * scale, height: PAGE_HEIGHT * scale }}
                >
                    <div className="pa-hoja-scale" style={{ transform: `scale(${scale})` }}>
                        <Hoja vm={vm}>
                            {index === 0 && <Portada vm={vm} tipoFoto={tipoFoto} />}
                            <Flujo vm={vm} piezas={piezas} portada={index === 0} />
                        </Hoja>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default CertificadoPeritajeAutomas;
